/* Shared patient helpers for Clara. Kept in their own module so the page, the
   composer, and the picker modal can all use them without importing each other. */

/* A patient's recordings, normalized across the two shapes the list endpoint returns.

   ``exists`` is the field that matters and the one Clara used to throw away. The server
   already runs os.path.exists against the real archive path for every recording, which
   is what greys a file out in Data Review's own "Select a Patient File" dialog. Clara
   received it in the same payload, kept the ids and names, dropped the rest, and then
   picked the most recent archive whether or not it was there. Two conversations died
   that way: seven signals promised from the database catalogue, every question answered
   "I don't have that data", because the files are on no disk we can reach.

   data_objects is checked FIRST. Both shapes carry ids, but only the clinical list adds
   data_file_exists alongside data_file_ids; the other deployment puts the same flag
   inside data_objects. Reading the arrays first would leave exists undefined and quietly
   default every recording to present, which is the bug being fixed.

   The null filter is load-bearing too: data_file_ids comes from json_agg over an OUTER
   join, so a patient with no archive at all arrives as [null], not [].
*/
export function recordingsOf(patient) {
	if (Array.isArray(patient.data_objects) && patient.data_objects.length > 0) {
		return patient.data_objects
			.map(object => ({
				id: object.id ?? object.obj_id,
				name: object.name ?? object.obj_name ?? object.file_name,
				exists: object.file_exists !== false,
				size: Number(object.filesize ?? object.size ?? 0),
			}))
			.filter(recording => recording.id != null)
	}
	if (Array.isArray(patient.data_file_ids)) {
		const exists = Array.isArray(patient.data_file_exists) ? patient.data_file_exists : []
		const sizes = Array.isArray(patient.data_file_sizes) ? patient.data_file_sizes : []
		return patient.data_file_ids
			.map((id, index) => ({
				id,
				name: patient.data_file_name?.[index],
				// Absent means unknown, and unknown has to read as present: a deployment
				// that does not report existence must not show every patient as unreadable.
				exists: exists[index] !== false,
				size: Number(sizes[index] || 0),
			}))
			.filter(recording => recording.id != null)
	}
	return []
}

// The recordings whose files are actually on the server.
export const readableRecordingsOf = patient => recordingsOf(patient).filter(recording => recording.exists)

// Clara can only watch a patient who has an archive at all.
export const hasRecording = patient => recordingsOf(patient).length > 0

// ...and can only answer for one whose archive can be read.
export const hasReadableRecording = patient => readableRecordingsOf(patient).length > 0

// What a patient is called in chat copy and in the composer chip.
export const displayOf = patient => patient.patient_name || patient.patient_id || patient.patient_key

// Patient labels remembered across sessions (key -> display name), used to name chats
// in the rail and to resolve a reopened chat's patient against the server.
export const LABEL_STORE = "clara.patientLabels"

export function readPatientLabels() {
	try {
		return JSON.parse(window.localStorage.getItem(LABEL_STORE) || "{}")
	} catch (error) {
		return {}
	}
}

// The patient list endpoint for this deployment. Clinical deployments have their own,
// leaner one (no prefetch status, no user tracking).
export const patientsEndpoint = (LINKS, isClinical) =>
	isClinical ? LINKS.DATA.PROFILING.GET_PATIENTS_CLINICAL : LINKS.DATA.PROFILING.GET_PATIENTS

/* Ask the SERVER for patients matching a term.

   Clara used to fetch one page of 300 sorted by patient id, then filter it in the
   browser. At UTSW that made the alphabetically first 300 the only patients that
   existed: searching "RR" for an admitted patient called RR9331CO found nothing, in
   the picker and in the composer both, while the same search in Data Visualize found
   them immediately. search_value has always been supported here (it matches the patient
   identifier, the site and the project, plus the name on clinical deployments). Nobody
   was passing it.

   Patients with no archive are dropped, because Clara has nothing to watch for them.
*/
export async function searchPatients(endpointProvider, endpoint, { term = "", limit = 25 } = {}) {
	const response = await endpointProvider.post(endpoint, {
		page: 0,
		page_size: limit,
		filters: { search_value: term },
		sort: [{ field: "patient_identifier", sort: "asc" }],
	})
	return (response?.data || []).filter(hasRecording)
}
